import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Crown, Send, Phone, AlertCircle, Plus, X as XIcon, ArrowLeft,
  MapPin, Calendar, Tag, Percent, ImageIcon, Globe,
  CheckCircle2, XCircle, Loader2
} from 'lucide-react';
import {
  TextField, Button,
  Checkbox, FormControlLabel, Slider, IconButton,
  Dialog, DialogContent
} from '@mui/material';
import Navbar from '../../components/Navbar';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import Config from '../../Config';
import { toast } from 'react-toastify';
import LocationModal from '../../utils/LocationModal';
import CategoryModal from '../../utils/CategoryModal';



const ConfirmationModal = ({ open, onClose, onConfirm, recipientCount }) => {
  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '16px'
        }
      }}
    >
      <DialogContent>
        <div className="flex flex-col items-center p-6 text-center">
          <div className="p-4 mb-6 bg-amber-50 rounded-full">
            <Send className="w-8 h-8 text-amber-600" />
          </div>
          <h2 className="mb-3 text-2xl font-bold text-gray-900">
            Confirm SMS Blast
          </h2>
          <p className="mb-6 text-gray-600">
            You are about to send SMS messages to <span className="font-semibold text-amber-600">{recipientCount.toLocaleString()}</span> subscribers. This action cannot be undone.
          </p>
          <div className="flex gap-4 w-full max-w-sm">
            <Button
              fullWidth
              onClick={onClose}
              sx={{
                color: '#4B5563',
                backgroundColor: '#F3F4F6',
                '&:hover': { backgroundColor: '#E5E7EB' },
                textTransform: 'none',
                borderRadius: '12px',
                fontWeight: 600,
                padding: '12px'
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              onClick={onConfirm}
              sx={{
                backgroundColor: '#F59E0B',
                color: 'white',
                '&:hover': { backgroundColor: '#D97706' },
                textTransform: 'none',
                borderRadius: '12px',
                fontWeight: 600,
                padding: '12px'
              }}
            >
              Confirm & Send
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Loading Modal Component
const LoadingModal = ({ open, status, onClose, onRetry }) => {
  return (
    <Dialog 
      open={open}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '16px',
          padding: '16px'
        }
      }}
    >
      <DialogContent>
        <div className="flex flex-col items-center p-6 text-center">
          {status === 'loading' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <div className="relative mb-6">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16"
                >
                  <Loader2 className="w-16 h-16 text-amber-600" />
                </motion.div>
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: [0.8, 1.2, 0.8] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="flex absolute inset-0 justify-center items-center"
                >
                  <Send className="w-8 h-8 text-amber-600" />
                </motion.div>
              </div>
              <h2 className="mb-3 text-2xl font-bold text-gray-900">
                Sending SMS Messages
              </h2>
              <p className="text-gray-600">
                Please wait while we deploy your SMS campaign...
              </p>
            </motion.div>
          )}
          {status === 'success' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <div className="p-4 mb-6 bg-green-50 rounded-full">
                <CheckCircle2 className="w-12 h-12 text-green-500" />
              </div>
              <h2 className="mb-3 text-2xl font-bold text-gray-900">
                SMS Blast Successful!
              </h2>
              <p className="mb-8 text-gray-600">
                Your SMS campaign has been successfully deployed.
              </p>
              <Button
                onClick={onClose}
                variant="contained"
                sx={{
                  backgroundColor: '#22C55E',
                  '&:hover': { backgroundColor: '#16A34A' },
                  textTransform: 'none',
                  borderRadius: '12px',
                  padding: '10px 24px',
                  fontWeight: 600
                }}
              >
                Close
              </Button>
            </motion.div>
          )}
          {status === 'error' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center"
            >
              <div className="p-4 mb-6 bg-red-50 rounded-full">
                <XCircle className="w-12 h-12 text-red-500" />
              </div>
              <h2 className="mb-3 text-2xl font-bold text-gray-900">
                SMS Blast Failed
              </h2>
              <p className="mb-8 text-gray-600">
                There was an error deploying your SMS campaign. Please try again.
              </p>
              <div className="flex gap-4">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  sx={{
                    borderColor: '#EF4444',
                    color: '#EF4444',
                    '&:hover': { 
                      backgroundColor: '#FEE2E2',
                      borderColor: '#DC2626'
                    },
                    textTransform: 'none',
                    borderRadius: '12px',
                    padding: '10px 24px',
                    fontWeight: 600
                  }}
                >
                  Close
                </Button>
                <Button
                  onClick={onRetry}
                  variant="contained"
                  sx={{
                    backgroundColor: '#EF4444',
                    '&:hover': { backgroundColor: '#DC2626' },
                    textTransform: 'none',
                    borderRadius: '12px',
                    padding: '10px 24px',
                    fontWeight: 600
                  }}
                >
                  Try Again
                </Button>
              </div>
            </motion.div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const BlastPremiumDeal = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testPhoneNumbers, setTestPhoneNumbers] = useState(['']);
  const [blastToAll, setBlastToAll] = useState(false);
  const [customBlastCount, setCustomBlastCount] = useState('');
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [metrics, setMetrics] = useState({
    totalSubscribers: 5000,
    previouslyBlasted: 2000,
    remainingSubscribers: 3000,
    coverage: 100
  });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const [blastStatus, setBlastStatus] = useState('loading');

  useEffect(()=>{
    const fetchDeal = async()=>{
      try{
        setLoading(true)
        const response = await axios.get(`${Config.baseUrl}/only/deals/${id}`)
        const data = await response?.data;
        if(data?.data?.status_code === "000"){
          setLoading(false)
          setDeal(data?.data?.data)
          const response_data = data?.data?.data;
          console.log("response_data", response_data.initial_blast)
          setMetrics({
            totalSubscribers: response_data.subscriber_count,
            previouslyBlasted: response_data.initial_blast || 0,
            remainingSubscribers: response_data.subscriber_count - response_data.initial_blast,
          })
        }else{
          toast.error(data?.data?.reason || "Deal details could not be fetched");
        }
      }catch(error){
        toast.error(error?.response?.data?.reason || "Something went wrong")
      }finally{
        setLoading(false)
      }
    }
    fetchDeal()
  },[id])

  const handleTestNumberChange = (index, value) => {
    const newNumbers = [...testPhoneNumbers];
    newNumbers[index] = value;
    setTestPhoneNumbers(newNumbers);
  };

  const handleAddTestNumber = () => {
    if (testPhoneNumbers.length < 5) {
      setTestPhoneNumbers([...testPhoneNumbers, '']);
    }
  };

  const handleRemoveTestNumber = (index) => {
    const newNumbers = testPhoneNumbers.filter((_, i) => i !== index);
    setTestPhoneNumbers(newNumbers);
  };

  const calculateRemainingToBlast = () => {
    if (blastToAll) {
      return metrics.remainingSubscribers;
    }
    return parseInt(customBlastCount) || 0;
  };

  useEffect(() => {
    if (blastToAll) {
      setCustomBlastCount(metrics.remainingSubscribers.toString());
    }
  }, [blastToAll, metrics.remainingSubscribers]);

  const handleBlastConfirm = async () => {
    setShowConfirmModal(false);
    setShowLoadingModal(true);
    setBlastStatus('loading');
    
    try {
      const response = await axios.post(
        `${Config.baseUrl}/blast/deal/${deal.premium_deal_slug}`,
        {
          quantity: calculateRemainingToBlast()
        }
      );

      const data = response?.data?.data;
      
      if (data?.status_code === "000") {
        setBlastStatus('success');
        toast.success("SMS blast deployed successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setBlastStatus('error');
        toast.error(data?.reason || "Failed to deploy SMS blast");
      }
    } catch (error) {
      setBlastStatus('error');
      toast.error(error?.response?.data?.data?.reason || "Something went wrong");
    }
  };

  const handleLoadingModalClose = () => {
    setShowLoadingModal(false);
    if (blastStatus === 'success') {
      window.location.reload();
    }
  };

  const handleRetry = () => {
    setShowLoadingModal(false);
    setShowConfirmModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="w-12 h-12 rounded-full border-b-2 border-amber-600 animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar page="Blast Premium Deals" />
      
      <LocationModal
        open={isLocationModalOpen}
        onClose={() => setIsLocationModalOpen(false)}
        locations={deal?.location || []}
      />

      <CategoryModal
        open={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        categories={deal?.category || []}
      />
      
      <div className="px-4 py-6 sm:px-6 lg:px-8 sm:py-8">
        <div className="mx-auto max-w-7xl">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="mb-6"
          >
            <Button
              onClick={() => navigate(-1)}
              startIcon={<ArrowLeft className="w-4 h-4" />}
              sx={{
                color: '#64748B',
                backgroundColor: 'white',
                borderRadius: '12px',
                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                '&:hover': {
                  backgroundColor: '#F8FAFC',
                },
                textTransform: 'none',
                fontWeight: 500,
                padding: '8px 16px',
              }}
            >
              Back to Premium Deals
            </Button>
          </motion.div>

          {/* Header Card */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-6 mb-8 bg-white rounded-2xl shadow-sm sm:p-8"
          >
            <div className="flex flex-col space-y-4 sm:flex-row sm:justify-between sm:items-center sm:space-y-0">
              <div className="flex gap-4 items-center">
                <div className="p-3 bg-amber-50 rounded-xl">
                  <Send className="w-6 h-6 text-amber-600 sm:w-7 sm:h-7" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 sm:text-3xl">Premium Deal SMS Blast</h1>
                  <p className="mt-1 text-sm text-gray-500 sm:text-base">Send your premium deals to targeted subscribers</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12">
            {/* Left Column - Test SMS */}
            <div className="lg:col-span-7">
              {/* Test SMS Panel */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="overflow-hidden h-full bg-white rounded-2xl shadow-sm"
              >
                <div className="p-6 sm:p-8">
                  {/* Deal Preview Section */}
                  <div className="pb-8 mb-8 border-b border-gray-100">
                    <div className="flex flex-col gap-6">
                      {/* Deal Image and Basic Info */}
                      <div className="flex-1">
                        <div className="flex gap-6 items-start">
                          {/* Image Section */}
                          <div className="overflow-hidden relative w-40 h-40 bg-gray-50 rounded-xl">
                            {deal.deal_image ? (
                              <img
                                src={deal.deal_image}
                                alt={deal.title}
                                className="object-cover w-full h-full transition-transform duration-300 hover:scale-105"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : (
                              <div className="flex flex-col justify-center items-center p-4 w-full h-full bg-gray-50">
                                <ImageIcon className="mb-2 w-8 h-8 text-gray-400" />
                                <p className="text-xs text-center text-gray-500">No image available</p>
                              </div>
                            )}
                            <div className="absolute top-2 left-2">
                              <span className="inline-flex gap-1 items-center px-2 py-1 text-xs font-medium text-amber-700 rounded-full backdrop-blur-sm bg-amber-100/90">
                                <Crown className="w-3 h-3" />
                                Premium
                              </span>
                            </div>
                          </div>

                          {/* Deal Info */}
                          <div className="flex-1">
                            <div className="flex gap-4 items-start">
                              <div className="p-3 bg-amber-50 rounded-xl">
                                <Crown className="w-6 h-6 text-amber-600" />
                              </div>
                              <div className="flex-1">
                                <h2 className="text-xl font-bold text-gray-900">{deal.title}</h2>
                                <p className="mt-1 text-sm text-gray-500">{deal.description.slice(0, 100)}...</p>
                                
                                <div className="flex flex-wrap gap-4 mt-4">
                                  <div className="flex gap-2 items-center">
                                    <MapPin className="w-4 h-4 text-gray-400" />
                                    <span className="text-sm text-gray-600">
                                      {deal.location && deal.location.length > 0 ? (
                                        deal.location[0].region === "all" ? (
                                          <div className="flex gap-2 items-center text-[12px]">
                                            <Globe className="w-4 h-4" />
                                            <span>Available Nationwide</span>
                                          </div>
                                        ) : (
                                          <div className="flex gap-1 items-center text-[12px]">
                                            <span>{`${deal.location[0].location}, ${deal.location[0].region} Region`}</span>
                                            {deal.location.length > 1 && (
                                              <Button
                                                variant="text"
                                                size="small"
                                                onClick={() => setIsLocationModalOpen(true)}
                                                startIcon={<Plus className="w-3 h-3" />}
                                                sx={{
                                                  minWidth: 'auto',
                                                  padding: '2px 4px',
                                                  color: '#F59E0B',
                                                  textTransform: 'none',
                                                  fontSize: '12px', 
                                                  '&:hover': {
                                                    backgroundColor: '#FEF3C7'
                                                  }
                                                }}
                                              >
                                                {deal.location.length - 1}
                                              </Button>
                                            )}
                                          </div>
                                        )
                                      ) : (
                                        "Location not specified"
                                      )}
                                    </span>
                                  </div>
                                  <div className="flex gap-2 items-center">
                                    <Calendar className="w-4 h-4 text-gray-400" />
                                    <span className="text-[12px] text-gray-600">
                                      Expires {new Date(deal.end_date).toLocaleDateString()}
                                    </span>
                                  </div>
                                  <div className="flex gap-2 items-center">
                                    <Tag className="w-4 h-4 text-gray-400" />
                                    <span className="text-[12px] text-gray-600">
                                      {deal.category.length > 0 ? (
                                        <div className="flex gap-2 items-center">
                                          <span>{deal.category[0]}</span>
                                          {deal.category.length > 1 && (
                                            <Button
                                              variant="text"
                                              size="small"
                                              onClick={() => setIsCategoryModalOpen(true)}
                                              startIcon={<Plus className="w-3 h-3" />}
                                              sx={{
                                                minWidth: 'auto',
                                                padding: '2px 8px',
                                                color: '#F59E0B',
                                                textTransform: 'none',
                                                '&:hover': {
                                                  backgroundColor: '#FEF3C7'
                                                }
                                              }}
                                            >
                                              {deal.category.length - 1}
                                            </Button>
                                          )}
                                        </div>
                                      ) : (
                                        "No categories"
                                      )}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Price and Stats */}
                      <div className="flex flex-col gap-4 p-4 bg-gray-50 rounded-xl">
                        <div className="flex justify-between items-center">
                          <div>
                            <div className="flex gap-2 items-baseline">
                              <span className="text-2xl font-bold text-amber-600">
                                ₵{Number(deal.discounted_price).toFixed(2)}
                              </span>
                              <span className="text-sm text-gray-500 line-through">
                                ₵{Number(deal.original_price).toFixed(2)}
                              </span>
                            </div>
                            <div className="flex gap-2 items-center mt-2">
                              <div className="p-1.5 bg-green-50 rounded-lg">
                                <Percent className="w-3 h-3 text-green-600" />
                              </div>
                              <span className="text-sm text-green-600">
                                Save {(Math.round((Number(deal.original_price) - Number(deal.discounted_price)) / Number(deal.original_price) * 100))}% on this deal
                              </span>
                            </div>
                          </div>
                          <div className="flex gap-6">
                            <div>
                              <p className="text-sm text-gray-500">Subscribers</p>
                              <p className="text-lg font-semibold text-gray-900">{deal.subscriber_count}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Currently Sent</p>
                              <p className="text-lg font-semibold text-gray-900">{deal.initial_blast ?  deal.initial_blast.toLocaleString() :  0}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Test SMS Section */}
                  <div className="flex gap-4 items-center mb-4">
                    <div className="p-3 bg-green-50 rounded-xl">
                      <Phone className="w-6 h-6 text-green-500" />
                    </div>
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Test SMS Campaign</h2>
                      <p className="mt-1 text-sm text-gray-500">Send a test message before the main blast</p>
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Info Alert */}
                    <div className="p-4 bg-green-50 rounded-xl border border-green-100">
                      <div className="flex gap-3 items-center">
                        <AlertCircle className="flex-shrink-0 w-5 h-5 text-green-600" />
                        <p className="text-sm text-green-700">
                          Add up to 5 phone numbers to test your SMS campaign
                        </p>
                      </div>
                    </div>

                    {/* Phone Number Inputs */}
                    <div className="space-y-4">
                      {testPhoneNumbers.map((number, index) => (
                        <div key={index} className="flex gap-3 items-center">
                          <TextField
                            label={`Phone ${index + 1}`}
                            value={number}
                            onChange={(e) => handleTestNumberChange(index, e.target.value)}
                            variant="outlined"
                            fullWidth
                            placeholder="Enter phone number"
                            size="small"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                                '& fieldset': {
                                  borderColor: '#E5E7EB',
                                },
                                '&:hover fieldset': { 
                                  borderColor: '#F59E0B' 
                                },
                                '&.Mui-focused fieldset': { 
                                  borderColor: '#F59E0B' 
                                }
                              },
                              '& .MuiInputLabel-root.Mui-focused': { 
                                color: '#F59E0B' 
                              }
                            }}
                          />
                          <IconButton 
                            onClick={() => handleRemoveTestNumber(index)}
                            className="flex-shrink-0"
                            sx={{
                              color: '#EF4444',
                              '&:hover': { 
                                backgroundColor: '#FEE2E2' 
                              }
                            }}
                          >
                            <XIcon className="w-5 h-5" />
                          </IconButton>
                        </div>
                      ))}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col gap-3 justify-between items-stretch pt-4 border-t border-gray-100 sm:flex-row">
                      <Button
                        variant="text"
                        disabled={testPhoneNumbers.length >= 5}
                        onClick={handleAddTestNumber}
                        startIcon={<Plus className="w-4 h-4" />}
                        sx={{
                          color: '#F59E0B',
                          '&:hover': { backgroundColor: '#FEF3C7' },
                          textTransform: 'none',
                        }}
                      >
                        Add Another Number
                      </Button>
                      <Button
                        variant="contained"
                        endIcon={<Send className="w-4 h-4" />}
                        sx={{
                          backgroundColor: '#F59E0B',
                          '&:hover': { backgroundColor: '#D97706' },
                          textTransform: 'none',
                          borderRadius: '12px',
                          padding: '8px 24px',
                        }}
                      >
                        Send Test SMS
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Right Column - Blast Controls */}
            <div className="lg:col-span-5">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                className="space-y-6"
              >
                {/* Blast Statistics Card */}
                <div className="overflow-hidden bg-white rounded-2xl shadow-sm">
                  <div className="p-6 sm:p-8">
                    <h3 className="mb-6 text-lg font-semibold text-gray-900">Blast Statistics</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-amber-50 rounded-xl">
                        <p className="mb-1 text-sm font-medium text-gray-600">Total Subscribers</p>
                        <p className="text-2xl font-bold text-amber-600">{metrics.totalSubscribers}</p>
                      </div>
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="mb-1 text-sm font-medium text-gray-600">Previously Blasted</p>
                        <p className="text-2xl font-bold text-blue-600">{metrics.previouslyBlasted ? metrics.previouslyBlasted.toLocaleString() : 0}</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-xl">
                        <p className="mb-1 text-sm font-medium text-gray-600">Remaining Subscribers</p>
                        <p className="text-2xl font-bold text-green-600">{metrics.remainingSubscribers}</p>
                      </div>
                      <div className="p-4 bg-purple-50 rounded-xl">
                        <p className="mb-1 text-sm font-medium text-gray-600">Selected to Blast</p>
                        <p className="text-2xl font-bold text-purple-600">{calculateRemainingToBlast()}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Blast Controls Card */}
                <div className="overflow-hidden bg-white rounded-2xl shadow-sm">
                  <div className="p-6 sm:p-8">
                    <div className="space-y-8">
                      {/* Blast Controls */}
                      <div className="space-y-6">
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={blastToAll}
                              onChange={(e) => setBlastToAll(e.target.checked)}
                              sx={{
                                '&.Mui-checked': { color: '#F59E0B' },
                              }}
                            />
                          }
                          label={
                            <span className="text-sm font-medium text-gray-700">
                              Blast to all subscribers ({metrics.remainingSubscribers})
                            </span>
                          }
                        />

                        {!blastToAll && (
                          <div className="space-y-6">
                            <div className="flex flex-col gap-2">
                              <TextField
                                fullWidth
                                label="Number of recipients"
                                value={customBlastCount}
                                onChange={(e) => {
                                  const inputValue = e.target.value;
                                  if (inputValue === '' || /^\d+$/.test(inputValue)) {
                                    const value = parseInt(inputValue) || 0;
                                    if (value >= 1 && value <= metrics.remainingSubscribers) {
                                      setCustomBlastCount(value.toString());
                                    } else if (value > metrics.remainingSubscribers) {
                                      setCustomBlastCount(metrics.remainingSubscribers.toString());
                                      toast.warning(`Maximum available recipients is ${metrics.remainingSubscribers}`);
                                    } else {
                                      setCustomBlastCount('1');
                                      toast.warning('Minimum number of recipients is 1');
                                    }
                                  }
                                }}
                                type="number"
                                InputProps={{
                                  inputProps: { 
                                    min: 1, 
                                    max: metrics.remainingSubscribers,
                                    step: 1
                                  }
                                }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '12px',
                                    '& fieldset': {
                                      borderColor: '#E5E7EB',
                                    },
                                    '&:hover fieldset': { 
                                      borderColor: '#F59E0B' 
                                    },
                                    '&.Mui-focused fieldset': { 
                                      borderColor: '#F59E0B' 
                                    }
                                  },
                                  '& .MuiInputLabel-root.Mui-focused': { 
                                    color: '#F59E0B' 
                                  }
                                }}
                              />
                              <div className="flex justify-between items-center px-1">
                                <span className="text-xs text-gray-500">Min: 1</span>
                                <span className="text-xs text-gray-500">Max: {metrics.remainingSubscribers}</span>
                              </div>
                            </div>

                            <div className="space-y-3">
                              <div className="flex justify-between items-center">
                                <span className="text-sm font-medium text-gray-500">1</span>
                                <span className="text-sm font-medium text-gray-500">{metrics.remainingSubscribers}</span>
                              </div>
                              <Slider
                                value={Math.max(parseInt(customBlastCount) || 1, 1)}
                                onChange={(_, value) => {
                                  setCustomBlastCount(value.toString());
                                }}
                                min={1}
                                max={metrics.remainingSubscribers}
                                marks={[
                                  { value: 1, label: '' },
                                  { value: Math.floor(metrics.remainingSubscribers / 2), label: '' },
                                  { value: metrics.remainingSubscribers, label: '' }
                                ]}
                                sx={{
                                  color: '#F59E0B',
                                  '& .MuiSlider-thumb': {
                                    borderColor: '#F59E0B',
                                  },
                                  '& .MuiSlider-rail': {
                                    backgroundColor: '#FDE68A',
                                  },
                                  '& .MuiSlider-track': {
                                    backgroundColor: '#F59E0B',
                                  },
                                  '& .MuiSlider-mark': {
                                    backgroundColor: '#F59E0B',
                                    height: '4px',
                                    width: '1px',
                                    '&.MuiSlider-markActive': {
                                      backgroundColor: '#FFFFFF',
                                    },
                                  }
                                }}
                              />
                              <div className="flex justify-between items-center px-1">
                                <span className="text-xs font-medium text-amber-600">
                                  Selected: {Math.max(parseInt(customBlastCount) || 1, 1).toLocaleString()} recipients
                                </span>
                                <span className="text-xs text-gray-500">
                                  {((Math.max(parseInt(customBlastCount) || 1, 1) / metrics.remainingSubscribers) * 100).toFixed(1)}% of available
                                </span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={() => setShowConfirmModal(true)}
                        disabled={!blastToAll && (!customBlastCount || parseInt(customBlastCount) < 1)}
                        sx={{
                          backgroundColor: '#F59E0B',
                          '&:hover': { backgroundColor: '#D97706' },
                          '&.Mui-disabled': {
                            backgroundColor: '#FDE68A',
                            color: '#F59E0B',
                          },
                          borderRadius: '12px',
                          padding: '12px',
                          textTransform: 'none',
                          fontWeight: 600,
                        }}
                      >
                        {`Send SMS Blast to ${blastToAll ? metrics.remainingSubscribers : Math.max(parseInt(customBlastCount) || 1, 1)} Subscribers`}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Confirmation Modal */}
                <ConfirmationModal
                  open={showConfirmModal}
                  onClose={() => setShowConfirmModal(false)}
                  onConfirm={handleBlastConfirm}
                  recipientCount={calculateRemainingToBlast()}
                />

                {/* Loading Modal */}
                <LoadingModal
                  open={showLoadingModal}
                  status={blastStatus}
                  onClose={handleLoadingModalClose}
                  onRetry={handleRetry}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlastPremiumDeal; 